import { useState, type ReactNode } from 'react';
import { move } from '../lib/reorder';

interface ReorderListProps<T> {
  items: T[];
  itemKey: (item: T, index: number) => string;
  itemLabel: (item: T, index: number) => string;
  onChange: (items: T[]) => void;
  renderItem: (item: T, index: number) => ReactNode;
  renderActions?: (item: T, index: number) => ReactNode;
  disabled?: boolean;
  label: string;
}

export function ReorderList<T>({
  items,
  itemKey,
  itemLabel,
  onChange,
  renderItem,
  renderActions,
  disabled,
  label,
}: ReorderListProps<T>) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const moveTo = (from: number, to: number, focusDirection?: 'up' | 'down') => {
    if (to < 0 || to >= items.length || from === to) return;
    onChange(move(items, from, to));
    const item = items[from];
    if (item !== undefined) setAnnouncement(`${itemLabel(item, from)} moved to position ${to + 1} of ${items.length}.`);
    if (focusDirection) {
      window.requestAnimationFrame(() => {
        const selector = `[data-reorder-list="${label}"] [data-reorder-index="${to}"] [data-move="${focusDirection}"]`;
        const target =
          document.querySelector<HTMLButtonElement>(`${selector}:not(:disabled)`) ??
          document.querySelector<HTMLButtonElement>(
            `[data-reorder-list="${label}"] [data-reorder-index="${to}"] [data-move]:not(:disabled)`,
          );
        target?.focus();
      });
    }
  };

  return (
    <>
      <ol className="reorder-list" aria-label={label} data-reorder-list={label}>
        {items.map((item, index) => {
          const name = itemLabel(item, index);
          const classes = ['reorder-item'];
          if (dragIndex === index) classes.push('dragging');
          if (overIndex === index && dragIndex !== null && dragIndex !== index) classes.push('drop-target');
          return (
            <li
              key={itemKey(item, index)}
              className={classes.join(' ')}
              data-reorder-index={index}
              draggable={!disabled}
              onDragStart={(event) => {
                setDragIndex(index);
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', String(index));
              }}
              onDragOver={(event) => {
                if (dragIndex === null) return;
                event.preventDefault();
                setOverIndex(index);
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (dragIndex !== null) moveTo(dragIndex, index);
                setDragIndex(null);
                setOverIndex(null);
              }}
              onDragEnd={() => {
                setDragIndex(null);
                setOverIndex(null);
              }}
            >
              {disabled ? null : (
                <span className="drag-handle" aria-hidden="true">
                  ⋮⋮
                </span>
              )}
              <div className="grow">{renderItem(item, index)}</div>
              {disabled ? null : (
                <div className="reorder-buttons">
                  <button
                    type="button"
                    className="btn btn-icon"
                    data-move="up"
                    onClick={() => moveTo(index, index - 1, 'up')}
                    disabled={index === 0}
                    aria-label={`Move ${name} up`}
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn btn-icon"
                    data-move="down"
                    onClick={() => moveTo(index, index + 1, 'down')}
                    disabled={index === items.length - 1}
                    aria-label={`Move ${name} down`}
                    title="Move down"
                  >
                    ↓
                  </button>
                </div>
              )}
              {renderActions ? renderActions(item, index) : null}
            </li>
          );
        })}
      </ol>
      <div className="visually-hidden" aria-live="polite">
        {announcement}
      </div>
    </>
  );
}
